import { db } from '@/db/db';
import type { NewPendingScan, PendingScan } from '@/db/models';

export const pendingScanRepository = {
  /** Writes a newly-captured scan immediately -- this is the "Save locally" step of the scan workflow. */
  async add(scan: NewPendingScan): Promise<void> {
    const record: PendingScan = {
      ...scan,
      uploaded: false,
      retryCount: 0,
      serverScanId: null,
      errorCode: null,
      errorMessage: null
    };

    await db.pendingScans.add(record);
  },

  /** All scans not yet terminally resolved -- what the sync engine (Stage 9) uploads each cycle. */
  async getPending(): Promise<PendingScan[]> {
    return db.pendingScans.filter((s) => !s.uploaded).sortBy('scannedAtUtc');
  },

  async getByLocalId(localId: string): Promise<PendingScan | undefined> {
    return db.pendingScans.get(localId);
  },

  /** Most recent scans first, for a local "recent activity" view that works even fully offline. */
  async getRecent(limit = 50): Promise<PendingScan[]> {
    return db.pendingScans.orderBy('scannedAtUtc').reverse().limit(limit).toArray();
  },

  async markUploaded(localId: string, serverScanId: string): Promise<void> {
    await db.pendingScans.update(localId, { uploaded: true, serverScanId, errorCode: null, errorMessage: null });
  },

  /** Terminal rejection (e.g. duplicate, unknown badge) -- stop retrying, but surface why. */
  async markRejected(localId: string, errorCode: string, errorMessage: string): Promise<void> {
    await db.pendingScans.update(localId, { uploaded: true, errorCode, errorMessage });
  },

  /** Transient failure (network/5xx) -- the whole batch will be retried later; just track attempts. */
  async incrementRetryCount(localId: string): Promise<void> {
    const existing = await db.pendingScans.get(localId);
    if (existing) {
      await db.pendingScans.update(localId, { retryCount: existing.retryCount + 1 });
    }
  },

  async updateNotes(
    localId: string,
    data: { notes: string | null; rating: PendingScan['rating'] }
  ): Promise<void> {
    await db.pendingScans.update(localId, data);
  },

  async countPending(): Promise<number> {
    return db.pendingScans.filter((s) => !s.uploaded).count();
  },

  async countUploadedSuccessfully(): Promise<number> {
    return db.pendingScans.filter((s) => s.uploaded && s.errorCode === null).count();
  },

  async countFailed(): Promise<number> {
    return db.pendingScans.filter((s) => s.uploaded && s.errorCode !== null).count();
  },

  async countScannedToday(): Promise<number> {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const startOfDayIso = startOfDay.toISOString();

    return db.pendingScans.where('scannedAtUtc').aboveOrEqual(startOfDayIso).count();
  }
};
